<script setup lang="ts">
import { computed } from 'vue'

import type { ProductSpec } from '@/api/ls'
import { quotaSummary } from '@/lib/quotas'

/**
 * 配額精簡摘要（批114 裁示 ⑯⑳）——**清單窄欄專用**。
 *
 * ## 為什麼有這個元件
 *
 * 批114 之前，每張清單都硬編三行「席次／協力／產品線」——那是**一個產品的欄位**寫進了
 * 後台的版面。改成資料驅動之後，配額鍵的數量是產品自己決定的（CS 有十一個），窄欄塞不下，
 * 所以只顯示前幾個、其餘 `+N`，滑鼠移上去看全部。
 *
 * - 排序與挑選規則全在 `lib/quotas.ts::quotaSummary`（有用量時「已達上限」優先、
 *   其次「接近上限」；已停用的鍵除非這張證真的帶著，否則不進摘要）。
 * - `usage` 吃任何形狀（授權的 `usage` 子物件、主機／部署列、null），內部走 `usageFrom`
 *   正規化——呼叫端不必先挑欄位。**不給＝只顯示上限**（裁示 ⑳：沒有用量資料的頁面）。
 * - 一行不折行、過長以省略號收尾：1366px 的筆電上，表格多一次折行就會把整頁推爆。
 */
const props = defineProps<{
  /** 該產品的憑證設計；null＝還沒載回來（退化成用原始英文鍵當標籤）。 */
  spec: ProductSpec | null
  /** 要顯示的上限值（有加購時請帶**有效值**）。 */
  quotas: Record<string, number | null> | null | undefined
  /** 心跳用量；不給／null＝只顯示上限。 */
  usage?: unknown
  /** 最多顯示幾項（其餘收成 `+N`）。預設 2。 */
  limit?: number
  /**
   * `quotas` 是**套裝模板**而不是一張證：缺鍵顯示「客製」（核發時由操作者填），
   * 不套產品的 `absent_default`。套裝表用。
   */
  template?: boolean
}>()

const sum = computed(() =>
  quotaSummary(props.spec, props.quotas, props.usage, { limit: props.limit, template: props.template }),
)

/** tooltip：一行一項列出全部配額（含被收進 `+N` 的那些）。 */
const tip = computed(() =>
  sum.value.all
    .map((q) => `${q.label} ${q.text}${q.over ? '（已達上限）' : q.near ? '（接近上限）' : ''}`)
    .join('\n'),
)
</script>

<template>
  <span v-if="!sum.all.length" class="subtle">—</span>
  <span v-else class="qsum" :title="tip">
    <template v-for="(q, i) in sum.items" :key="q.key">
      <span v-if="i" class="qsum-sep">・</span>
      <span class="qsum-i" :class="{ over: q.over, near: q.near }">{{ q.label }} {{ q.text }}</span>
    </template>
    <template v-if="sum.rest > 0">
      <span class="qsum-sep">・</span>
      <span class="qsum-rest">+{{ sum.rest }}</span>
    </template>
  </span>
</template>

<style scoped>
.qsum {
  display: block;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-variant-numeric: tabular-nums;
  cursor: help;
}
.qsum-sep {
  color: var(--fg-4);
  margin: 0 1px;
}
/* 已達上限：與 `.chip.over`（styles/ui.css）同一個顏色語彙 */
.qsum-i.over {
  color: var(--danger);
  font-weight: 600;
}
/* 接近上限（≥80%）：橘字提醒，不是錯誤 */
.qsum-i.near {
  color: var(--warning, var(--fg-2));
  font-weight: 600;
}
.qsum-rest {
  color: var(--fg-3);
}
</style>
